import axios from 'axios';
import Cookies from 'js-cookie';

// Create axios instance with base configuration
export const apiClient = axios.create({
  // Add /api to baseURL since nginx proxies /api to backend
  baseURL: import.meta.env.VITE_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
  withCredentials: true, // Always send cookies with requests
});

// Log the baseURL being used
console.log('[apiClient] Using baseURL:', apiClient.defaults.baseURL);

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    // Ensure headers object exists
    if (!config.headers) {
      config.headers = {};
    }
    
    // Try to get token from cookies
    // auth_token is httpOnly and might not be accessible
    let token = Cookies.get('auth_token');

    // If no auth_token, try demo_token cookie (accessible to JS)
    if (!token) {
      token = Cookies.get('demo_token');
    }

    // Fallback to sessionStorage for older demo sessions
    if (!token && localStorage.getItem('isDemo') === 'true') {
      token = sessionStorage.getItem('demo_token');
    }

    // IMPORTANT: Only set Authorization header if we actually have a token
    // Don't set "Bearer null" or "Bearer undefined" - let the httpOnly cookie be used instead
    if (token && token !== 'null' && token !== 'undefined') {
      config.headers.Authorization = `Bearer ${token}`;
      // Debug logging for production
      console.log('[apiClient] Request with auth:', {
        url: config.url,
        hasToken: !!token,
        tokenPreview: token.substring(0, 20) + '...'
      });
    } else {
      console.log('[apiClient] Request without auth header (will use httpOnly cookie if present):', {
        url: config.url,
        cookieExists: !!Cookies.get('auth_token'),
        isDemo: localStorage.getItem('isDemo') === 'true'
      });
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Store the current demo status before clearing
      const wasDemo = localStorage.getItem('isDemo') === 'true';
      const hasDemoToken = !!Cookies.get('demo_token') || !!sessionStorage.getItem('demo_token');
      const currentPath = window.location.pathname;
      const requestUrl = error.config?.url || '';
      
      console.log('[apiClient] 401/403 interceptor:', {
        url: requestUrl,
        status: error.response?.status,
        isDemo: wasDemo,
        hasDemoToken: hasDemoToken,
        currentPath: currentPath
      });
      
      // Don't redirect if we're already on a login page or plan selection
      if (currentPath.includes('/login') ||
          currentPath.includes('/dancing-queen') ||
          currentPath.includes('/demo/select-plan')) {
        console.log('[apiClient] Skipping redirect - already on login page');
        return Promise.reject(error);
      }

      // Don't redirect for VH Staff users - they have separate auth
      const userType = localStorage.getItem('user_type');
      if (userType === 'vh_staff' || currentPath.includes('/vh-staff') || currentPath.includes('/staff/') || currentPath.includes('/virtual-host/')) {
        console.log('[apiClient] Skipping redirect - VH Staff user');
        return Promise.reject(error);
      }
      
      // Don't redirect for demo V2 or V3 API calls
      if (requestUrl.includes('/demo/v2/') || requestUrl.includes('/demo/v3/')) {
        console.log('[apiClient] Skipping redirect - demo API call');
        return Promise.reject(error);
      }
      
      // Don't redirect for frictionless onboarding API calls (pre-auth flow)
      if (requestUrl.includes('/frictionless-onboarding/') || 
          requestUrl.includes('extract-multiple-preview') ||
          requestUrl.includes('search-complete')) {
        console.log('[apiClient] Skipping redirect - frictionless onboarding API call');
        return Promise.reject(error);
      }
      
      // Don't redirect for dashboard-specific endpoints that might return 403 for permission reasons
      // These should be handled gracefully by the components
      if (requestUrl.includes('/customer-insights/') || 
          requestUrl.includes('/analytics/questions-answered') ||
          requestUrl.includes('/analytics/impact')) {
        console.log('[apiClient] Skipping redirect - dashboard endpoint with permission check');
        return Promise.reject(error);
      }
      
      // For demo users, NEVER redirect to restaurant-login
      if (wasDemo || hasDemoToken) {
        // Don't redirect on any protected pages for demo users
        if (currentPath.includes('/restaurant-dashboard') || 
            currentPath.includes('/analytics') || 
            currentPath.includes('/menu') ||
            currentPath.includes('/settings')) {
          console.log('[apiClient] Demo user on protected page - not redirecting, letting component handle error');
          return Promise.reject(error);
        }
        
        console.log('[apiClient] Demo session expired, redirecting to demo login');
        window.location.href = '/dancing-queen';
      } else {
        console.log('[apiClient] Will redirect to restaurant login');
        
        // Clear auth data only for non-demo users
        Cookies.remove('auth_token');
        
        // For regular users, redirect to restaurant login
        console.log('[apiClient] OAuth session expired, redirecting to restaurant login');
        window.location.href = '/restaurant-login';
      }
    }
    return Promise.reject(error);
  }
);